import * as Log from "@opencode-ai/core/util/log"
import { Global } from "@opencode-ai/core/global"
import { InstallationVersion } from "@opencode-ai/core/installation/version"
import { Telemetry } from "@kilocode/kilo-telemetry"
import { migrateLegacyKiloAuth } from "@kilocode/kilo-gateway"
import { Config } from "@/config/config"
import { Auth } from "@/auth"
import { Filesystem } from "@/util/filesystem"
import { Database } from "@/storage/db"
import { ProductDb } from "@/kilocode/storage/product-db"
import { JsonMigration } from "@/storage/json-migration"
import { drizzle } from "drizzle-orm/bun-sqlite"
import { EOL } from "node:os"
import { InstanceStore } from "@/project/instance-store"
import { InfcodeHookDispatcher } from "@/infcode/hooks/dispatcher"
import { shutdownExportersFinal } from "@/infcode/shutdown-exporters"
import { InfcodeV1Migration } from "@/infcode/migration"

const LOG_PREFIX = "[cli:lifecycle]"
const log = Log.create({ service: LOG_PREFIX })

async function shouldRunJsonMigration(marker: string): Promise<boolean> {
  if (await Filesystem.exists(marker)) return false
  // Product DB channels seed themselves from the legacy shared DB on first open.
  // If a seed source exists, running JSON migration in the same startup only
  // repeats work against an already-migrated copy.
  return !ProductDb.hasSeedSource({
    root: Global.Path.data,
    target: marker,
    platform: process.env["INFCODE_PLATFORM"],
    variant: process.env["INFCODE_JETBRAINS_PRODUCT_CODE"] ?? process.env["INFCODE_PLATFORM_VARIANT"],
  })
}

export async function initializeCliServices() {
  // kilocode_change start - Initialize telemetry
  const globalCfg = await Config.getGlobal()
  await Telemetry.init({
    dataPath: Global.Path.data,
    version: InstallationVersion,
    enabled: globalCfg.experimental?.openTelemetry !== false,
  })

  // Migrate legacy Kilo CLI auth if needed
  await migrateLegacyKiloAuth(
    async () => (await Auth.get("kilo")) !== undefined,
    async (auth) => Auth.set("kilo", auth),
  )

  const kiloAuth = await Auth.get("kilo")
  if (kiloAuth) {
    const token = kiloAuth.type === "oauth" ? kiloAuth.access : kiloAuth.key
    const accountId = kiloAuth.type === "oauth" ? kiloAuth.accountId : undefined
    await Telemetry.updateIdentity(token, accountId)
  }

  Telemetry.trackCliStart()
  // kilocode_change end

  // infcode_change start
  // Use the active database channel as the one-time JSON migration marker.
  const marker = Database.Path
  // infcode_change end
  if (await shouldRunJsonMigration(marker)) {
    // infcode_change
    const tty = process.stderr.isTTY
    process.stderr.write("Performing one time database migration, may take a few minutes..." + EOL)
    const width = 36
    const orange = "\x1b[38;5;214m"
    const muted = "\x1b[0;2m"
    const reset = "\x1b[0m"
    let last = -1
    if (tty) process.stderr.write("\x1b[?25l")
    try {
      await JsonMigration.run(drizzle({ client: Database.Client().$client }), {
        progress: (event) => {
          const percent = Math.floor((event.current / event.total) * 100)
          if (percent === last && event.current !== event.total) return
          last = percent
          if (tty) {
            const fill = Math.round((percent / 100) * width)
            const bar = `${"■".repeat(fill)}${"･".repeat(width - fill)}`
            process.stderr.write(
              `\r${orange}${bar} ${percent.toString().padStart(3)}%${reset} ${muted}${event.label.padEnd(12)} ${event.current}/${event.total}${reset}`,
            )
            if (event.current === event.total) process.stderr.write("\n")
          } else {
            process.stderr.write(`sqlite-migration:${percent}${EOL}`)
          }
        },
      })
    } finally {
      if (tty) process.stderr.write("\x1b[?25h")
      else {
        process.stderr.write(`sqlite-migration:done${EOL}`)
      }
    }
    process.stderr.write("Database migration complete." + EOL)
  }
  if (!process.argv.includes("generate")) InfcodeV1Migration.startInBackground({ projectDir: process.cwd() }) // infcode_change
}

export async function shutdownCliServices() {
  // kilocode_change start - Track CLI exit before interrupting active turns.
  const exitCode = typeof process.exitCode === "number" ? process.exitCode : undefined
  Telemetry.trackCliExit(exitCode)
  // kilocode_change end

  // infcode_change - safety-net shutdown must preserve interruption semantics for any active turn.
  await InstanceStore.disposeAllInstancesInterrupting()
  // infcode_change start - disposal emits final lifecycle hooks; drain them before closing exporters.
  // The primary drain now lives inside disposeAllInstancesInterrupting so TUI/global-shutdown paths
  // are covered too; this call stays as a safety net for anything enqueued after that drain.
  const hooksDrained = await InfcodeHookDispatcher.drainAsync(2_000)
  if (!hooksDrained) log.warn("timed out draining async hooks during shutdown", { timeoutMs: 2_000 })
  // The process really is exiting here, so this is the one place that stops the exporters for good.
  // disposeAllInstancesInterrupting only *flushed* them (it runs while disposers are still going),
  // so everything recorded since then is still queued and gets sent by this call. Single-flight, so
  // other exit paths can call it too without rebuilding Langfuse's OTel providers.
  // infcode_change start - 需求 7144132630：退出收尾阶段埋点 WASM 的 Rust 日志会刷到终端。
  // TUI 退出会把 console 还原成原生实现，而 flush 发生在这之后（下一行的 shutdownExportersFinal），
  // 于是 `INFO src/client/mod.rs:… flush events at front end` 这类行直接打印在终端上。flush 之前
  // 把日志过滤器重新装上，只吞 Rust 日志行，其它 console 输出照旧。
  try {
    const { installMetricsWasmConsoleBridge } = await import("@/infcode/metrics/console-bridge")
    installMetricsWasmConsoleBridge({ force: true })
  } catch (error) {
    // 日志过滤失败不影响退出
    log.warn("failed to reinstall metrics console bridge during shutdown", { error: String(error) })
  }
  // infcode_change end
  await shutdownExportersFinal()
  // infcode_change（评审 F2）- 独立 CLI 本地浏览器 runtime 不在 MCP Service 注册表内，
  // 没有随实例释放的 finalizer：退出前在这里回收 playwright-mcp/Chrome 进程树。
  {
    const { localBrowserBridge } = await import("@/infcode/browser/runtime/local/local-browser-bridge")
    await localBrowserBridge
      .disposeAll()
      .catch((error: unknown) =>
        log.warn("local browser runtime teardown failed during shutdown", { error: String(error) }),
      )
  }
  // infcode_change end
}
